import React, { Component } from 'react';
import Logo from '../../assets/LOGO FINAL verde.png';
import {
  GetSchedulePresenceByToken,
  RespondSchedulePresenceByToken
} from '../../infra/requests/ScheduleRequests';
import {
  Container,
  Card,
  Logo as LogoImg,
  Title,
  Message,
  Details,
  DetailRow,
  DetailLabel,
  DetailValue,
  Actions,
  ActionButton,
  DangerButton,
  SecondaryButton,
  SuccessIcon,
  PlatformLink
} from './VisitPresencePageStyles';

/**
 * Página pública para onde apontam os botões "Confirmar"/"Recusar" do email das 72h.
 *
 * Os textos vivem aqui, e não nas traduções da plataforma, porque a língua tem de
 * acompanhar a do email que o casal recebeu (`wedding.language`, devolvida pela API)
 * e não a língua activa no portal — o casal pode nem sequer estar autenticado.
 */
const TEXTS = {
  pt: {
    title: 'Confirmação de presença',
    processing: 'A processar a sua resposta...',
    labelDate: 'Data',
    labelTime: 'Hora',
    labelPlace: 'Local',
    online: 'Online',
    timeSuffix: 'hora Lisboa - GMT',
    pendingTitle: 'Confirma que não vai estar presente?',
    pendingText: 'Ao confirmar, avisamos já a nossa equipa. Se afinal puder estar presente, é só escolher a outra opção.',
    refuseButton: 'Não vou estar presente',
    confirmInsteadButton: 'Afinal vou estar presente',
    confirmedTitle: 'Presença confirmada',
    confirmedText: 'Obrigado! Já registámos a vossa confirmação. Até breve!',
    alreadyConfirmedText: 'A vossa presença neste atendimento já estava confirmada.',
    refusedTitle: 'Resposta registada',
    refusedText: 'Obrigado por nos avisar. A nossa equipa já foi informada. Para remarcar, aceda à plataforma.',
    alreadyRefusedText: 'Já nos tinham indicado que não iriam estar presentes neste atendimento.',
    expiredTitle: 'Já não é possível responder',
    expiredText: 'Este atendimento já decorreu. Se precisar de marcar um novo, aceda à plataforma.',
    cancelledTitle: 'Atendimento cancelado',
    cancelledText: 'Este atendimento foi entretanto cancelado. Se precisar de remarcar, aceda à plataforma.',
    notConfirmedTitle: 'Atendimento por confirmar',
    notConfirmedText: 'Este atendimento ainda não está confirmado. A nossa equipa entrará em contacto consigo.',
    invalidTitle: 'Ligação inválida',
    invalidText: 'Esta ligação não é válida. Pode consultar e responder aos seus atendimentos na plataforma.',
    errorTitle: 'Ocorreu um erro',
    errorText: 'Não foi possível registar a sua resposta. Tente novamente ou responda através da plataforma.',
    retry: 'Tentar novamente',
    platformLink: 'Ir para a plataforma'
  },
  en: {
    title: 'Attendance confirmation',
    processing: 'Processing your answer...',
    labelDate: 'Date',
    labelTime: 'Time',
    labelPlace: 'Venue',
    online: 'Online',
    timeSuffix: 'Lisbon time - GMT',
    pendingTitle: 'Confirm that you will not attend?',
    pendingText: 'Once you confirm, we will let our team know. If you can attend after all, just pick the other option.',
    refuseButton: 'I will not attend',
    confirmInsteadButton: 'I will attend after all',
    confirmedTitle: 'Attendance confirmed',
    confirmedText: 'Thank you! Your confirmation has been recorded. See you soon!',
    alreadyConfirmedText: 'Your attendance at this appointment was already confirmed.',
    refusedTitle: 'Answer recorded',
    refusedText: 'Thank you for letting us know. Our team has been informed. To reschedule, please go to the platform.',
    alreadyRefusedText: 'You had already told us you would not be attending this appointment.',
    expiredTitle: 'It is no longer possible to answer',
    expiredText: 'This appointment has already taken place. If you need a new one, please go to the platform.',
    cancelledTitle: 'Appointment cancelled',
    cancelledText: 'This appointment has since been cancelled. If you need to reschedule, please go to the platform.',
    notConfirmedTitle: 'Appointment not confirmed yet',
    notConfirmedText: 'This appointment is not confirmed yet. Our team will get in touch with you.',
    invalidTitle: 'Invalid link',
    invalidText: 'This link is not valid. You can check and answer your appointments on the platform.',
    errorTitle: 'Something went wrong',
    errorText: 'We could not record your answer. Please try again or answer through the platform.',
    retry: 'Try again',
    platformLink: 'Go to the platform'
  }
};

const browserLanguage = () => {
  try {
    return String(navigator.language || '').toLowerCase().startsWith('pt') ? 'pt' : 'en';
  } catch (e) {
    return 'pt';
  }
};

class VisitPresencePage extends Component {
  state = {
    loading: true,
    submitting: false,
    // Estado devolvido pela API: PENDING | CONFIRMED | REFUSED | EXPIRED | CANCELLED | NOT_CONFIRMED
    data: null,
    // Distingue "acabou de responder" de "já tinha respondido antes"
    answeredNow: false,
    error: null,
    token: null
  };

  componentDidMount() {
    const query = new URLSearchParams(this.props.location.search);
    const token = query.get('token');
    const action = String(query.get('action') || '').toLowerCase();

    if (!token) {
      return this.setState({ loading: false, error: 'INVALID_TOKEN' });
    }

    this.setState({ token }, () => {
      // Confirmar é inofensivo e não tem volta a dar: aplica-se logo.
      // Recusar exige um clique explícito, para que um toque acidental não
      // marque o casal como ausente
      if (action === 'confirm') return this.respond('CONFIRM');
      return this.load();
    });
  }

  load = async () => {
    const { token } = this.state;
    this.setState({ loading: true });

    const result = await GetSchedulePresenceByToken(token);
    if (!result?.success) {
      return this.setState({
        loading: false,
        error: result?.message === 'INVALID_TOKEN' ? 'INVALID_TOKEN' : 'GENERIC'
      });
    }

    this.setState({ loading: false, data: result?.data, error: null });
  };

  respond = async action => {
    const { token } = this.state;
    this.setState({ submitting: true, loading: true, error: null });

    const result = await RespondSchedulePresenceByToken(token, action);
    if (!result?.success) {
      return this.setState({
        loading: false,
        submitting: false,
        error: result?.message === 'INVALID_TOKEN' ? 'INVALID_TOKEN' : 'GENERIC'
      });
    }

    this.setState({
      loading: false,
      submitting: false,
      data: result?.data,
      answeredNow: !result?.data?.alreadyAnswered,
      error: null
    });
  };

  getTexts = () => TEXTS[this.state.data?.language] || TEXTS[browserLanguage()];

  renderDetails = () => {
    const { data } = this.state;
    const texts = this.getTexts();
    if (!data) return null;

    return (
      <Details>
        <DetailRow>
          <DetailLabel>{texts.labelDate}</DetailLabel>
          <DetailValue>{`${data.weekdayName}, ${data.date}`}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{texts.labelTime}</DetailLabel>
          <DetailValue>{`${data.time} (${texts.timeSuffix})`}</DetailValue>
        </DetailRow>
        <DetailRow>
          <DetailLabel>{texts.labelPlace}</DetailLabel>
          <DetailValue>{data.isOnline ? texts.online : data.weddingPlace}</DetailValue>
        </DetailRow>
      </Details>
    );
  };

  renderContent = () => {
    const { loading, submitting, data, answeredNow, error } = this.state;
    const texts = this.getTexts();

    if (loading) return <Message>{texts.processing}</Message>;

    if (error) {
      const isInvalid = error === 'INVALID_TOKEN';
      return (
        <>
          <Title>{isInvalid ? texts.invalidTitle : texts.errorTitle}</Title>
          <Message>{isInvalid ? texts.invalidText : texts.errorText}</Message>
          {!isInvalid && (
            <Actions>
              <ActionButton onClick={this.load}>{texts.retry}</ActionButton>
            </Actions>
          )}
        </>
      );
    }

    switch (data?.status) {
      case 'CONFIRMED':
        return (
          <>
            <SuccessIcon>✓</SuccessIcon>
            <Title>{texts.confirmedTitle}</Title>
            {this.renderDetails()}
            <Message>{answeredNow ? texts.confirmedText : texts.alreadyConfirmedText}</Message>
          </>
        );

      case 'REFUSED':
        return (
          <>
            <Title>{texts.refusedTitle}</Title>
            {this.renderDetails()}
            <Message>{answeredNow ? texts.refusedText : texts.alreadyRefusedText}</Message>
          </>
        );

      case 'PENDING':
        return (
          <>
            <Title>{texts.pendingTitle}</Title>
            {this.renderDetails()}
            <Message>{texts.pendingText}</Message>
            <Actions>
              <DangerButton
                disabled={submitting}
                onClick={() => this.respond('REFUSE')}
              >
                {texts.refuseButton}
              </DangerButton>
              <SecondaryButton
                disabled={submitting}
                onClick={() => this.respond('CONFIRM')}
              >
                {texts.confirmInsteadButton}
              </SecondaryButton>
            </Actions>
          </>
        );

      case 'EXPIRED':
        return (
          <>
            <Title>{texts.expiredTitle}</Title>
            {this.renderDetails()}
            <Message>{texts.expiredText}</Message>
          </>
        );

      case 'CANCELLED':
        return (
          <>
            <Title>{texts.cancelledTitle}</Title>
            {this.renderDetails()}
            <Message>{texts.cancelledText}</Message>
          </>
        );

      case 'NOT_CONFIRMED':
        return (
          <>
            <Title>{texts.notConfirmedTitle}</Title>
            {this.renderDetails()}
            <Message>{texts.notConfirmedText}</Message>
          </>
        );

      default:
        return (
          <>
            <Title>{texts.errorTitle}</Title>
            <Message>{texts.errorText}</Message>
          </>
        );
    }
  };

  render() {
    const { data } = this.state;
    const texts = this.getTexts();

    return (
      <Container>
        <Card>
          <LogoImg src={Logo} alt="logo" />
          {this.renderContent()}
          <PlatformLink href={data?.platformLink || '/visit-list'}>
            {texts.platformLink}
          </PlatformLink>
        </Card>
      </Container>
    );
  }
}

export default VisitPresencePage;
